/**
 * Chat multi-file attachments (MYAC-1182) — a sibling to `chatbot.ts` rather than
 * more state piled into it (that store is already 1400+ lines): this file owns
 * the pending-upload state and the `__ATTACH__` wire marker; `chatbot.ts` only
 * calls `takeMarkerBatch()` from `sendMessage`, the same shape as `takeFocus()`.
 *
 * FORMATS (MYAC-1398): PDF, Word, Excel, PowerPoint, CSV and text — `ATTACHMENT_FORMATS`
 * below, keyed by extension like the server's table. The chatbot gets an office file
 * converted before the model reads it; this store only has to save the bytes with
 * the right content type.
 *
 * THE UPLOAD IS A RAW SAVE, NOT AN INGESTION: the files land on
 * `projects/{pid}/chat_uploads/{batchId}/{n}__{filename}` — the dedicated
 * rules block (Arslene, 2026-09-10; replaces the 2026-09-08 reuse of
 * MYAC-1119's `redline_sources` shelf). No datasource segment — the chat has
 * no datasource — and the ingestion trigger does not match it (spec §1,
 * verified against `eventarc.py:196-204`). The marker's `v: 2` tells the
 * chatbot which grammar the bytes are on; the server still derives the path
 * itself. No uploads-doc is written here — there is nothing for
 * the pipeline to pick up until the user explicitly asks to add a file to the
 * corpus (a later PR's `AttachmentAddCard`, out of scope here).
 *
 * Ceilings are enforced CLIENT-SIDE for UX only (spec §2) — the chatbot callback
 * re-validates server-side, same as every other front-side gate in this app.
 */

import { ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import { defineStore } from 'pinia'
import { useFirebaseStorage } from 'vuefire'
import { logger } from '@/utils/logger'

/** The wire prefix — mirrors `FOCUS_PREFIX` in `chatbot.ts` exactly (MYAC-919). */
/** `crypto.randomUUID` exists only in secure contexts (https / localhost) —
 *  a dev server reached over plain http://<ip> has `getRandomValues` but not it. */
function randomUuid(): string {
    const c = globalThis.crypto
    if (typeof c?.randomUUID === 'function') return c.randomUUID()
    const b = new Uint8Array(16)
    c.getRandomValues(b)
    b[6] = (b[6] & 0x0f) | 0x40
    b[8] = (b[8] & 0x3f) | 0x80
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'))
    return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`
}

export const ATTACH_PREFIX = '__ATTACH__'

export const MAX_ATTACHMENTS_PER_TURN = 4
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024

/** The family a file belongs to — what the chip's icon and the hint name. */
export type AttachmentFamily = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'csv' | 'text'

export interface AttachmentFormat {
    /** The lower-cased extension — the key the whole platform routes on. */
    ext: string
    family: AttachmentFamily
    /** The content type the upload is saved with (and the storage rule checks). */
    mime: string
    /** What a browser may report in `File.type` for this extension: the canonical
     *  type plus the known aliases. An EMPTY `File.type` is accepted for every
     *  format (browsers leave it blank for types they do not know). */
    accepts: readonly string[]
}

/**
 * MYAC-1398 — the accepted formats, keyed by extension, mirroring the server's table
 * (`core_agents_tools.tools.chat_attachments_tools.ATTACHMENT_FORMATS`): the marker
 * carries no MIME, so the EXTENSION is the contract on both ends. The drop
 * (MYAC-1369) has no `accept` filter — every dropped file reaches `classifyFile` —
 * so a format is added HERE and nowhere else; `ATTACHMENT_ACCEPT` derives from
 * this table. The strings that NAME the formats to the user (the drop hint and
 * the `unsupported_*` refusals, `chat.attachments.*` in en.json and fr.json) are
 * locale literals: a format added here is added to them by hand.
 */
export const ATTACHMENT_FORMATS: readonly AttachmentFormat[] = [
    { ext: 'pdf', family: 'pdf', mime: 'application/pdf', accepts: ['application/pdf'] },
    { ext: 'doc', family: 'word', mime: 'application/msword', accepts: ['application/msword'] },
    {
        ext: 'docx', family: 'word',
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        accepts: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    },
    { ext: 'xls', family: 'excel', mime: 'application/vnd.ms-excel', accepts: ['application/vnd.ms-excel'] },
    {
        ext: 'xlsx', family: 'excel',
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        accepts: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    },
    { ext: 'ppt', family: 'powerpoint', mime: 'application/vnd.ms-powerpoint', accepts: ['application/vnd.ms-powerpoint'] },
    {
        ext: 'pptx', family: 'powerpoint',
        mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        accepts: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    },
    // Windows with Excel installed reports a CSV as Excel's own type.
    { ext: 'csv', family: 'csv', mime: 'text/csv', accepts: ['text/csv', 'application/vnd.ms-excel', 'text/plain'] },
    { ext: 'txt', family: 'text', mime: 'text/plain', accepts: ['text/plain'] },
]

/** The format of a filename by its extension, null when it is not accepted. */
export function attachmentFormat(filename: string): AttachmentFormat | null {
    const name = filename ?? ''
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : ''
    return ATTACHMENT_FORMATS.find((f) => f.ext === ext) ?? null
}

/** What the paperclip's `<input accept>` offers: every extension AND every
 *  canonical type, so the picker filters the same set `classifyFile` accepts. */
export const ATTACHMENT_ACCEPT = [
    ...ATTACHMENT_FORMATS.map((f) => `.${f.ext}`),
    ...ATTACHMENT_FORMATS.map((f) => f.mime),
].join(',')

/**
 * One key per refusal, each naming what was refused and what to do instead
 * (`chat.attachments.errors.*`, en + fr). The `unsupported_*` families cover the
 * types people actually drop on the chat; anything else is `unsupported_type`,
 * which names the accepted formats. A raw extension or MIME never reaches the
 * screen (front/CLAUDE.md: no technical keys in user-facing text).
 */
export type AttachmentErrorKind =
    | 'too_many'
    | 'too_large'
    | 'unsupported_type'
    | 'unsupported_image'
    | 'unsupported_archive'
    | 'unsupported_opendocument'
    | 'upload_failed'

const _IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tif', 'tiff', 'webp', 'heic', 'svg'])
const _ARCHIVE_EXTS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'tgz'])
const _OPENDOCUMENT_EXTS = new Set(['odt', 'ods', 'odp'])

/** Which refusal a NOT-accepted file gets — by extension first, by the browser's
 *  type family second (an image with an odd extension still reads as an image). */
export function unsupportedKind(file: Pick<File, 'name' | 'type'>): AttachmentErrorKind {
    const name = file.name ?? ''
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : ''
    if (_IMAGE_EXTS.has(ext) || file.type.startsWith('image/')) return 'unsupported_image'
    if (_ARCHIVE_EXTS.has(ext)) return 'unsupported_archive'
    if (_OPENDOCUMENT_EXTS.has(ext) || file.type.startsWith('application/vnd.oasis.opendocument')) {
        return 'unsupported_opendocument'
    }
    return 'unsupported_type'
}

export interface PendingAttachment {
    /** Stable key for chip actions (retry/remove) — not part of the wire contract. */
    id: string
    /**
     * 1-based position among the files that will actually upload — the `n` in
     * both the storage path and the marker's `files[].index`. `null` for a file
     * that never counted (over the 4-file ceiling, too large, wrong type): it
     * never occupies a slot, so removing it and dropping another keeps the
     * numbering of the files that DO upload contiguous.
     */
    index: number | null
    filename: string
    size: number
    /** The accepted format's family — the chip's icon; null for a refused file. */
    family: AttachmentFamily | null
    file: File
    /** 0–100. Live byte-level percentage from the resumable upload's
     *  `state_changed` snapshots (see `attachmentProgressPercent`); 100 once saved. */
    progress: number
    error: AttachmentErrorKind | null
    done: boolean
}

/** The wire form's file entry — no gs:// path, ever (spec §3: the server derives it). */
export interface ChatAttachmentMarkerFile {
    index: number
    filename: string
    size: number
}

export interface ChatAttachmentMarker {
    /** Path-grammar version: 2 = the dedicated `chat_uploads/` block. The
     *  chatbot treats an absent `v` as the legacy grammar, so old persisted
     *  turns keep resolving. */
    v: number
    batch_id: string
    files: ChatAttachmentMarkerFile[]
}

/** The wire form. `chatbot.ts` prepends this the same way it prepends `focusMarker()`. */
export function attachMarker(batch: ChatAttachmentMarker): string {
    return `${ATTACH_PREFIX}${JSON.stringify(batch)}`
}

/** The message without its attach-marker line. Mirrors `stripFocusMarker` exactly. */
export function stripAttachMarker(text: string): string {
    if (!text.startsWith(ATTACH_PREFIX)) return text
    const newline = text.indexOf('\n')
    return newline === -1 ? '' : text.slice(newline + 1)
}

/** Reads the batch back off a sent turn's own text. Mirrors `readFocusMarker` exactly. */
export function readAttachMarker(text: string | undefined): ChatAttachmentMarker | null {
    if (!text?.startsWith(ATTACH_PREFIX)) return null
    const newline = text.indexOf('\n')
    const raw = newline === -1 ? text.slice(ATTACH_PREFIX.length) : text.slice(ATTACH_PREFIX.length, newline)
    try {
        const parsed = JSON.parse(raw) as Partial<ChatAttachmentMarker>
        return parsed?.batch_id && Array.isArray(parsed.files) ? (parsed as ChatAttachmentMarker) : null
    } catch {
        // A marker we cannot read is a marker we do not label — never a crash in
        // the middle of rendering a conversation.
        return null
    }
}

/** "1.2 MB" — chip display only, not part of the wire contract. */
/** Upload progress as a whole percentage from a resumable-upload snapshot.
 *  Guards `totalBytes === 0` (a 0-byte file, or a snapshot before the total is
 *  known) to 0 rather than NaN. Exported so the math is unit-tested directly. */
export function attachmentProgressPercent(bytesTransferred: number, totalBytes: number): number {
    return totalBytes > 0 ? Math.round((bytesTransferred / totalBytes) * 100) : 0
}

export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    const units = ['KB', 'MB', 'GB']
    let value = bytes / 1024
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex++
    }
    return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`
}

/** The accepted format whose known browser types include `type`, if any. */
function formatForBrowserType(type: string): AttachmentFormat | null {
    return ATTACHMENT_FORMATS.find((f) => f.accepts.includes(type)) ?? null
}

function classifyFile(file: File, validCountSoFar: number): AttachmentErrorKind | null {
    // The EXTENSION decides the format (the server routes on it, and the upload is
    // saved with the extension's canonical type whatever the browser said). The
    // browser's `File.type` is only a tie-breaker against a RENAMED file: it is
    // refused when it names ANOTHER accepted format (`report.pdf` reported as Word).
    // Anything else — blank, generic (`application/octet-stream`, `application/zip`
    // for a .docx on a machine without a MIME database), an alias such as
    // `application/csv` — is not a disagreement (review of PR 978).
    const format = attachmentFormat(file.name)
    if (!format) return unsupportedKind(file)
    const claimed = file.type && !format.accepts.includes(file.type) ? formatForBrowserType(file.type) : null
    if (claimed && claimed.family !== format.family) return 'unsupported_type'
    if (file.size > MAX_ATTACHMENT_BYTES) return 'too_large'
    if (validCountSoFar >= MAX_ATTACHMENTS_PER_TURN) return 'too_many'
    return null
}

export const useChatAttachmentsStore = defineStore('chatAttachments', {
    state: () => ({
        pendingAttachments: [] as PendingAttachment[],
        attachBatchId: null as string | null,
    }),

    getters: {
        hasPendingAttachments: (state): boolean => state.pendingAttachments.length > 0,
        /** Every attachment saved to Storage — nothing errored, nothing still in flight. */
        allUploaded: (state): boolean => state.pendingAttachments.every((a) => a.done),
        /** Send is blocked while this is true: something is still uploading, or
         *  failed and has not been retried or removed (spec BUILD 1). */
        blocksSend(): boolean {
            return this.hasPendingAttachments && !this.allUploaded
        },
    },

    actions: {
        /**
         * Validate and queue a drop, then start uploading whatever passed.
         * `projectId`/`sessionId` are the caller's responsibility (ChatInterface.vue
         * needs no session: the storage path carries no session segment, so a
         * file can be picked before any message exists — the session is created
         * when the turn SENDS, never by attaching).
         */
        // csm-event: none — the turn this batch rides on is already counted by
        // `chat_message_sent` when it sends; a picked-but-unsent file is not yet
        // engagement. Per-attachment analytics is a later pass if wanted.
        addAttachmentFiles(files: File[], projectId: string): void {
            if (!files.length || !projectId) return
            if (!this.attachBatchId) this.attachBatchId = randomUuid()
            const batchId = this.attachBatchId

            let validCountSoFar = this.pendingAttachments.filter((a) => a.index !== null).length
            const toUpload: PendingAttachment[] = []

            for (const file of files) {
                const error = classifyFile(file, validCountSoFar)
                const pending: PendingAttachment = {
                    id: randomUuid(),
                    index: error ? null : ++validCountSoFar,
                    filename: file.name,
                    size: file.size,
                    family: error ? null : attachmentFormat(file.name)!.family,
                    file,
                    progress: 0,
                    error,
                    done: false,
                }
                this.pendingAttachments.push(pending)
                // THE reactivity trap (live bug, 2026-09-09): `push` stores the raw
                // object but the store exposes a reactive PROXY around it. Mutating
                // the raw `pending` from uploadOne updates the data yet TRIGGERS
                // nothing — no re-render, no recompute — so the chip said
                // "uploading…" forever and send stayed blocked, while the bytes were
                // long since saved. Upload against the proxy, never the raw object.
                const reactivePending = this.pendingAttachments[this.pendingAttachments.length - 1]
                if (!error) toUpload.push(reactivePending)
            }

            // Parallel, per-file error captured (spec BUILD 1) — each upload catches
            // its own failure, so Promise.all here never rejects on one bad file.
            void Promise.all(toUpload.map((p) => this.uploadOne(projectId, batchId, p)))
        },

        async uploadOne(projectId: string, batchId: string, pending: PendingAttachment): Promise<void> {
            try {
                const storage = useFirebaseStorage()
                const path = `projects/${projectId}/chat_uploads/${batchId}/${pending.index}__${pending.filename}`
                // Resumable, NOT the simple `uploadBytes`: the simple multipart-POST
                // endpoint hangs indefinitely on dev (App Check / CORS on that path)
                // even though the bytes land, so the promise never resolves and send
                // stays blocked. The datasource upload (uploads.ts) uses the resumable
                // path and works — mirror it. Bonus: real byte-level progress.
                // The CANONICAL type of the extension, never `File.type` (which a
                // browser may leave blank or alias) — the storage rule checks it.
                const task = uploadBytesResumable(storageRef(storage, path), pending.file, {
                    contentType: attachmentFormat(pending.filename)?.mime ?? 'application/octet-stream',
                })
                await new Promise<void>((resolve, reject) => {
                    task.on(
                        'state_changed',
                        (snap) => {
                            pending.progress = attachmentProgressPercent(
                                snap.bytesTransferred, snap.totalBytes,
                            )
                        },
                        (err) => reject(err),
                        () => resolve(),
                    )
                })
                pending.progress = 100
                pending.done = true
                pending.error = null
            } catch (error) {
                logger.error('Failed to upload a chat attachment', error)
                pending.progress = 0
                pending.done = false
                pending.error = 'upload_failed'
            }
        },

        /** Re-run the upload for one failed file. No-op on a ceiling violation
         *  (wrong type / too large / too many) — those can only be removed. */
        retryAttachment(id: string, projectId: string): void {
            const pending = this.pendingAttachments.find((a) => a.id === id)
            if (!pending || pending.error !== 'upload_failed' || !this.attachBatchId) return
            pending.error = null
            pending.progress = 0
            void this.uploadOne(projectId, this.attachBatchId, pending)
        },

        // csm-event: none — undo of a not-yet-sent pick (a mis-drop or a "changed
        // my mind"), not product engagement worth tracking (mirrors deleteRun's
        // reasoning in standardRedline.ts).
        removeAttachment(id: string): void {
            this.pendingAttachments = this.pendingAttachments.filter((a) => a.id !== id)
            if (this.pendingAttachments.length === 0) this.attachBatchId = null
        },

        clearAttachments(): void {
            this.pendingAttachments = []
            this.attachBatchId = null
        },

        /**
         * Hand the batch to the message being sent, and drop it — the same
         * "one message, one attachment" gesture as `chatbot.ts`'s `takeFocus()`.
         * Returns null when there is nothing to send (no pending files) or
         * nothing SENDABLE yet (still uploading / a failure not yet resolved) —
         * the caller (`sendMessage`) checks `blocksSend` first and must not reach
         * here in the blocked case.
         */
        takeMarkerBatch(): ChatAttachmentMarker | null {
            if (!this.hasPendingAttachments || !this.allUploaded || !this.attachBatchId) return null
            const batch: ChatAttachmentMarker = {
                v: 2,
                batch_id: this.attachBatchId,
                files: this.pendingAttachments.map((a) => ({
                    index: a.index as number,
                    filename: a.filename,
                    size: a.size,
                })),
            }
            this.clearAttachments()
            return batch
        },
    },
})
